import {ChangeDetectionStrategy, Component, OnDestroy, OnInit, inject, input, output} from '@angular/core';
import {FormControl, FormGroup} from '@angular/forms';
import * as moment from 'moment-timezone';
import {Subscription} from 'rxjs';
import {TimezoneService} from 'src/app/services/timezone.service';
import {OphIconModule} from 'src/app/shared/design/oph-icon/oph-icon.module';
import {OphTimeSelectorComponent} from 'src/app/shared/design/oph-time-selector/oph-time-selector.component';
import {PipesModule} from 'src/app/shared/pipes/pipes.module';
import {TaskCalendarComponent} from '../../calendar/task-calendar.component';
import {ProjectTaskDialogPanels} from '../project-task-dialog-constants';
import {ProjectsTaskNewItemWrapperComponent} from '../new-item-wrapper/projects-task-new-item-wrapper.component';

@Component({
  selector: 'projects-task-date-time-picker',
  standalone: true,
  imports: [
    TaskCalendarComponent,
    OphTimeSelectorComponent,
    ProjectsTaskNewItemWrapperComponent,
    PipesModule,
    OphIconModule,
  ],
  templateUrl: './projects-task-date-time-picker.component.html',
  styleUrl: './projects-task-date-time-picker.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ProjectsTaskDateTimePickerComponent implements OnInit, OnDestroy {
  dateTimeControl = input<FormControl>();
  newTask = input<boolean>();
  disableTimeSelector = input<boolean>();
  form = input<FormGroup>();
  minDate = input<moment.Moment>();

  changePanel = output<ProjectTaskDialogPanels>();

  ProjectTaskDialogPanels = ProjectTaskDialogPanels;

  // Form controls consolidated into a single FormGroup
  dateTimeForm = new FormGroup({
    date: new FormControl<moment.Moment | null>(null),
    hours: new FormControl<string>('12'),
    minutes: new FormControl<string>('01'),
    ampm: new FormControl<string>('AM'),
  });

  storedDateTime: string;

  private subscriptions: Subscription[] = [];

  private timezoneService = inject(TimezoneService);

  ngOnInit() {
    this.setScheduleDateTime();
    this.storedDateTime = this.dateTimeControl()?.value;
  }

  ngOnDestroy() {
    this.subscriptions.forEach(sub => sub.unsubscribe());
  }

  setScheduleDateTime() {
    const dateMoment = moment(this.dateTimeControl()?.value).tz(this.timezoneService.getTimeZone());
    const hours = dateMoment.format('h'); // 12-hour format
    const minutes = dateMoment.format('mm');
    const ampm = dateMoment.format('A'); // Returns 'AM' or 'PM'

    this.dateTimeForm.patchValue({
      date: dateMoment,
      hours,
      minutes,
      ampm,
    });
  }

  setTime(dateMoment: moment.Moment) {
    const hours = dateMoment.format('h'); // 12-hour format
    const minutes = dateMoment.format('mm');
    const ampm = dateMoment.format('A'); // Returns 'AM' or 'PM'

    this.dateTimeForm.patchValue({
      hours,
      minutes,
      ampm,
    });
  }

  onDateChange(dateMoment: moment.Moment) {
    this.dateTimeForm.get('date')?.setValue(dateMoment);
  }

  onSave() {
    const date = this.dateTimeForm.get('date')?.value;
    const hours = this.dateTimeForm.get('hours')?.value;
    const minutes = this.dateTimeForm.get('minutes')?.value;
    const ampm = this.dateTimeForm.get('ampm')?.value;

    if (!date) return;

    // Create moment in Eastern timezone from the Date object
    const scheduleMoment = moment(date).tz(this.timezoneService.getTimeZone());
    let hour24 = parseInt(hours || '12');

    // Convert to 24-hour format
    if (ampm === 'AM' && hour24 === 12) {
      hour24 = 0;
    } else if (ampm === 'PM' && hour24 !== 12) {
      hour24 = hour24 + 12;
    }

    scheduleMoment.set({
      hour: hour24,
      minute: parseInt(minutes || '0'),
      second: 0,
      millisecond: 0,
    });

    this.dateTimeControl()?.setValue(this.timezoneService.convertDateToUtcString(scheduleMoment.toDate()));

    this.changePanel.emit(this.newTask() ? ProjectTaskDialogPanels.NewSchedule : ProjectTaskDialogPanels.Default);
  }

  onCancel() {
    // Restore the stored schedule date to the form
    this.dateTimeControl()?.setValue(this.storedDateTime);
    this.changePanel.emit(this.newTask() ? ProjectTaskDialogPanels.NewSchedule : ProjectTaskDialogPanels.NewBasicInfo);
  }

  // Getter methods for template access
  get dateControl() {
    return this.dateTimeForm.get('date');
  }

  get hoursControl() {
    return this.dateTimeForm.get('hours');
  }

  get minutesControl() {
    return this.dateTimeForm.get('minutes');
  }

  get ampmControl() {
    return this.dateTimeForm.get('ampm');
  }
}
